import { TabbedPageLayoutBody } from '@/modules/application/components/TabbedPageLayoutBody';
import { TabbedPageLayout } from '@/modules/application/layouts/TabbedPageLayout';
import { FunctionComponent } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { movementService } from '../api/movements.service';
import { MovementModel } from '../types/MovementModel';
import FilterBar from '@/components/filterbar/FilterBar';
import { FilterFieldName, FilterValues } from '@/components/filterbar/FilterBarContext';
import LocationFilter from '@/components/filterbar/filters/LocationFilter';
import { useTranslation } from '@/lib';
import { PagePaginationResultDto, PageSortOption, PageSortOrder } from '@/lib/api/pagination.page.dto';
import useQueryParamsFilters from '@/hooks/useQueryParamFilters';
import { useSearchParams } from 'react-router';
import { CacheKey } from '@/providers/cache-provider/cache-key.enum';
import { MovementsIndividualGrid } from '../components/MovementsIndividualGrid';
import { SortChangedEvent, GridReadyEvent } from 'ag-grid-community';
import { useExportToExcel } from '@/hooks/useExportToExcel';
import dayjs from 'dayjs';
import AssetFilter from '@/components/filterbar/filters/AssetFilter';
import DateFilter from '@/components/filterbar/filters/DateFilter';
import { MovementFilterOptionsDTO, MovementSortOption, MovementType } from '../api/movements.contracts';
import { ColumnID } from '@/components/grid/column-ids';
import PaginationControls from '@/components/grid/PaginationControls';
import PagedResultDataText from '@/components/filterbar/PagedResultDataText';

const MOVEMENT_FETCH_LIMIT = 1000;

interface MovementsOverviewPageProps {}

export const MovementsOverviewPage: FunctionComponent<MovementsOverviewPageProps> = () => {
  const { t } = useTranslation();
  const { exportToExcel } = useExportToExcel();

  const [searchParams] = useSearchParams();

  const { filters, setFiltersToUrl } = useQueryParamsFilters(
    {
      locationId: searchParams.get('locationId') ? Number(searchParams.get('locationId')) : undefined,
      assetId: searchParams.get('assetId') ? Number(searchParams.get('assetId')) : undefined,
      dateFrom: searchParams.get('dateFrom') ? dayjs(searchParams.get('dateFrom')!) : undefined,
      dateTo: searchParams.get('dateTo') ? dayjs(searchParams.get('dateTo')!) : undefined,
      pageNumber: searchParams.get('pageNumber') ? Number(searchParams.get('pageNumber')) : 1,
      sortDirection: searchParams.get('sortDirection') as PageSortOrder,
      sortOption: searchParams.get('sortOption') as PageSortOption,
    },
    CacheKey.MOVEMENT_FILTERS,
  );

  const { data, isLoading, setApiCallArg } = useApiCall<PagePaginationResultDto<MovementModel>>(() => {
    const filterOptions: MovementFilterOptionsDTO = {
      locationId: filters.current.locationId,
      assetId: filters.current.assetId,
      dateFrom: filters.current.dateFrom?.toISOString(),
      dateTo: filters.current.dateTo?.toISOString(),
      type: MovementType.INCOMING,
    };

    return movementService.get(filterOptions, {
      page: filters.current.pageNumber ?? 1,
      limit: MOVEMENT_FETCH_LIMIT,
      order: filters.current.sortDirection ?? PageSortOrder.DESC,
      sort: filters.current.sortOption ?? MovementSortOption.OCCURRED_AT,
    });
  });

  function onFilterChange(_filterValues: FilterValues) {
    // Can be used to react to filter changes without triggering search
  }

  const handleSearch = (filterValues: FilterValues, page?: number) => {
    setFiltersToUrl({
      ...filterValues,
      pageNumber: page ?? filterValues.pageNumber ?? 1,
    });
    searchWithCurrentFilterValues({
      ...filterValues,
      pageNumber: page ?? filterValues.pageNumber ?? 1,
    });
  };

  const onPageChanged = (_event: React.ChangeEvent<unknown>, page: number) => {
    handleSearch(
      {
        ...filters.current,
        pageNumber: page,
      },
      page,
    );
  };

  function searchWithCurrentFilterValues(filterValues: FilterValues) {
    if (!filterValues) {
      return;
    }

    const filterOptions: MovementFilterOptionsDTO = {
      locationId: filterValues.locationId,
      assetId: filterValues.assetId,
      dateFrom: filterValues.dateFrom?.toISOString(),
      dateTo: filterValues.dateTo?.toISOString(),
      type: MovementType.INCOMING,
    };

    setApiCallArg(() =>
      movementService.get(filterOptions, {
        page: filterValues.pageNumber ?? 1,
        limit: MOVEMENT_FETCH_LIMIT,
        order: filterValues.sortDirection,
        sort: filterValues.sortOption,
      }),
    );
  }

  function onSortOptionChanged(event: SortChangedEvent<MovementModel>) {
    if (event.columns && event.columns.length > 0 && event.source === 'uiColumnSorted') {
      const changedColumn = event.columns[0];
      const changedColumnId = changedColumn.getColId();

      let sortOption: MovementSortOption | string | undefined;
      switch (changedColumnId) {
        case ColumnID.MOVEMENT_OCCURRED_AT:
          sortOption = MovementSortOption.OCCURRED_AT;
          break;
        case ColumnID.ASSET_CODE:
          sortOption = MovementSortOption.ASSET_CODE;
          break;
        case ColumnID.FROM_LOCATION_NAME:
          sortOption = MovementSortOption.FROM_LOCATION;
          break;
        case ColumnID.TO_LOCATION_NAME:
          sortOption = MovementSortOption.TO_LOCATION;
          break;
        case ColumnID.EVENT_TYPE:
          sortOption = MovementSortOption.EVENT_TYPE;
          break;
        case ColumnID.EVENT_SOURCE:
          sortOption = MovementSortOption.EVENT_SOURCE;
          break;
        default:
          sortOption = changedColumnId;
          break;
      }

      let sortOrder: PageSortOrder | undefined;
      switch (changedColumn.getSort()) {
        case 'asc':
          sortOrder = PageSortOrder.ASC;
          break;
        case 'desc':
          sortOrder = PageSortOrder.DESC;
          break;
        default:
          sortOption = undefined;
          break;
      }

      setFiltersToUrl({
        ...filters.current,
        sortOption,
        sortDirection: sortOrder,
      });

      handleSearch(
        {
          ...filters.current,
          sortOption,
          sortDirection: sortOrder,
        },
        1,
      );
    }
  }

  const onGridReady = (event: GridReadyEvent<MovementModel>) => {
    // Apply initial sort state
    if (filters.current.sortOption && filters.current.sortDirection) {
      let columnId: ColumnID | undefined;

      switch (filters.current.sortOption) {
        case MovementSortOption.OCCURRED_AT:
          columnId = ColumnID.MOVEMENT_OCCURRED_AT;
          break;
        case MovementSortOption.ASSET_CODE:
          columnId = ColumnID.ASSET_CODE;
          break;
        case MovementSortOption.FROM_LOCATION:
          columnId = ColumnID.FROM_LOCATION_NAME;
          break;
        case MovementSortOption.TO_LOCATION:
          columnId = ColumnID.TO_LOCATION_NAME;
          break;
        case MovementSortOption.EVENT_TYPE:
          columnId = ColumnID.EVENT_TYPE;
          break;
        case MovementSortOption.EVENT_SOURCE:
          columnId = ColumnID.EVENT_SOURCE;
          break;
        default:
          break;
      }

      if (columnId) {
        event.api.applyColumnState({
          state: [
            {
              colId: columnId,
              sort: filters.current.sortDirection === PageSortOrder.ASC ? 'asc' : 'desc',
            },
          ],
          defaultState: { sort: null },
        });
      }
    }
  };

  const handleExportToExcel = async () => {
    const allData: MovementModel[] = [];
    const filterOptions: MovementFilterOptionsDTO = {
      locationId: filters.current.locationId,
      assetId: filters.current.assetId,
      dateFrom: filters.current.dateFrom?.toISOString(),
      dateTo: filters.current.dateTo?.toISOString(),
      type: MovementType.INCOMING,
    };

    // Fetch first page
    const dataPage1 = await movementService.get(filterOptions, {
      page: 1,
      limit: MOVEMENT_FETCH_LIMIT,
      order: filters.current.sortDirection,
      sort: filters.current.sortOption,
    });

    if (dataPage1.isSuccess) {
      allData.push(...dataPage1.payload.data);

      // Fetch additional pages if needed
      for (let i = 2; i <= (dataPage1.payload?.totalPages || 1); i++) {
        const dataPage = await movementService.get(filterOptions, {
          page: i,
          limit: MOVEMENT_FETCH_LIMIT,
          order: filters.current.sortDirection,
          sort: filters.current.sortOption,
        });

        if (dataPage.isSuccess) {
          allData.push(...dataPage.payload.data);
        }
      }

      const exportData = allData.map((movement) => ({
        [t('occurred_at')]: movement.occurredAt ? new Date(movement.occurredAt).toLocaleString() : '',
        [t('asset_code')]: movement.asset?.code,
        [t('asset_type_code')]: movement.asset?.assetType?.code,
        [t('from_location')]: movement.fromLocation?.name,
        [t('to_location')]: movement.toLocation?.name,
        [t('event_type')]: movement.event?.type ? t(`event.type.${movement.event.type}`) : undefined,
        [t('event_source')]: movement.event?.source ? t(`enums.event_source.${movement.event.source}`) : undefined,
      }));

      exportToExcel(exportData, `movements_${dayjs().format('YYYY_MM_DD_HHmmss')}.xlsx`);
    }
  };

  const filterComponents = [
    {
      label: t('location'),
      filterName: FilterFieldName.locationId,
      filter: <LocationFilter label={t('location')} />,
      embedded: true,
      searchOnChange: true,
    },
    {
      label: t('asset'),
      filterName: FilterFieldName.assetId,
      filter: <AssetFilter label={t('asset')} />,
      embedded: true,
      searchOnChange: true,
    },
    {
      label: t('date_from'),
      filterName: FilterFieldName.dateFrom,
      filter: <DateFilter label={t('date_from')} filterField={FilterFieldName.dateFrom} />,
    },
    {
      label: t('date_to'),
      filterName: FilterFieldName.dateTo,
      filter: <DateFilter label={t('date_to')} filterField={FilterFieldName.dateTo} />,
    },
  ];

  return (
    <TabbedPageLayout>
      <TabbedPageLayoutBody>
        <div className="flex h-full grow flex-col">
          <div className="mb-2 flex items-center">
            <FilterBar
              onSearch={handleSearch}
              onChange={onFilterChange}
              initialFilterValues={filters.current}
              showAsPopover
              isLoading={isLoading}
              filterComponents={filterComponents}
            ></FilterBar>
          </div>
          <MovementsIndividualGrid
            data={data?.data}
            isLoading={isLoading}
            onSortChanged={onSortOptionChanged}
            onGridReady={onGridReady}
            onExportToExcelClicked={handleExportToExcel}
          />
          <div className="flex items-center gap-x-4 mt-2">
            <PaginationControls
              isLoading={isLoading}
              totalPageCount={data?.totalPages ?? 1}
              currentPage={filters.current.pageNumber ?? 1}
              totalElements={data?.totalElements ?? 0}
              onChange={onPageChanged}
            />
            <PagedResultDataText data={data} name={t('movement', { count: data?.totalElements ?? 0 })} />
          </div>
        </div>
      </TabbedPageLayoutBody>
    </TabbedPageLayout>
  );
};
